import React from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { Link } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { __t } from '../../../i18n/t'
import type { Receipt } from './model'
import { Rich } from './ui/basics'
import { OutlineButton, PrimaryButton } from './ui/controls'
import { C, kitCardSx } from './ui/tokens'

/** Tudo o que a tela de salvo mostra, calculado na hora de salvar (depois disso o Padrão em vigor já mudou). */
export interface SavedInfo {
  code: string
  title: string
  status: string
  /** Frase do resumo, com **negrito**. */
  sentence: string
  receipts: Receipt[]
  note: string
}

// Estilo fora de ternário: o plugin de i18n embrulha literal com espaço dentro de condicional (ver ui/tokens.ts).
const actionsNarrowSx = { display: 'flex', flexWrap: 'wrap', gap: 1.25, '& > *': { flex: '1 1 100%' } } as const
const actionsWideSx = { display: 'flex', flexWrap: 'wrap', gap: 1.25, '& > *': { flex: '0 0 auto' } } as const
const sentenceBoxSx = { p: '16px 18px', borderRadius: '12px', bgcolor: C.surface, border: `1px solid ${C.line}`, fontSize: 15, lineHeight: 1.6, color: C.ink, overflowWrap: 'anywhere' } as const
const receiptsSx = { listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 } as const
const receiptSx = { display: 'flex', gap: 1.25, alignItems: 'flex-start', fontSize: 14, color: '#334155', lineHeight: 1.45 } as const
const receiptIconSx = { fontSize: 18, color: '#2e7d32', mt: '1px', flexShrink: 0 } as const
const receiptWarnSx = { ...receiptIconSx, color: '#ed6c02' } as const
const savedLabelSx = { fontSize: 12.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.greenDark } as const

const pop = keyframes`
  0% { transform: scale(.6); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); }
`
const enter = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`

/** Depois de salvar: o código, o que de fato aconteceu e os caminhos seguintes (criar outro, duplicar, ver a lista). */
export const SavedScreen: React.FC<{ info: SavedInfo; narrow: boolean; onNew: () => void; onDuplicate: () => void }> = ({ info, narrow, onNew, onDuplicate }) => (
  <Box
    component="section"
    aria-labelledby="ok-t"
    sx={{
      ...kitCardSx, p: narrow ? 2.5 : 4, display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0,
      animation: `${enter} .35s cubic-bezier(.22,1,.36,1) both`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box
        aria-hidden
        sx={{
          width: 56, height: 56, borderRadius: '50%', bgcolor: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
          animation: `${pop} .5s cubic-bezier(.22,1,.36,1) both`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <CheckIcon sx={{ fontSize: 32, color: C.ink }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box component="span" sx={savedLabelSx}>{__t('Desconto salvo')}</Box>
          <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, bgcolor: '#f1f5f9', border: `1px solid ${C.line}`, borderRadius: 999, px: 1.25, py: '2px', fontVariantNumeric: 'tabular-nums' }}>{info.code}</Box>
        </Box>
        <Typography id="ok-t" component="h2" tabIndex={-1} sx={{ fontSize: 24, fontWeight: 700, color: C.ink, overflowWrap: 'anywhere', outline: 'none' }}>{info.title}</Typography>
      </Box>
    </Box>

    <Box sx={{ fontSize: 15, color: '#334155', lineHeight: 1.5 }}>{info.status}</Box>

    <Box sx={sentenceBoxSx}>
      <Rich text={info.sentence} />
    </Box>

    {info.receipts.length ? (
      <Box component="ul" sx={receiptsSx}>
        {info.receipts.map((r, i) => (
          <Box component="li" key={i} sx={receiptSx}>
            {r.tone === 'ok'
              ? <TaskAltIcon aria-hidden sx={receiptIconSx} />
              : <WarningAmberIcon aria-hidden sx={receiptWarnSx} />}
            <span>{r.text}</span>
          </Box>
        ))}
      </Box>
    ) : null}

    <Box sx={narrow ? actionsNarrowSx : actionsWideSx}>
      <PrimaryButton onClick={onNew} icon={<AddIcon />}>{__t('Criar outro desconto')}</PrimaryButton>
      <OutlineButton onClick={onDuplicate} icon={<ContentCopyIcon />}>{__t('Duplicar este desconto')}</OutlineButton>
      <OutlineButton component={Link} extra={{ to: '/discounts' }}>{__t('Ver lista de descontos')}</OutlineButton>
    </Box>

    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', fontSize: 13, color: C.muted, lineHeight: 1.45 }}>
      <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, flex: 'none', mt: '1px' }} />
      <span>{info.note}</span>
    </Box>
  </Box>
)
